// ---------------------------------------------------------------------------
// Shared status badge component
// ---------------------------------------------------------------------------

// Precision Blue semantic tints. Status is never color-only: the mono uppercase
// label carries the meaning, color reinforces it. Five semantic families:
//   success = verified / positive     info = informational / in-flight (azure)
//   warning = needs attention         danger = negative / failed     neutral = inert
const POSITIVE = 'bg-success-tint text-success border border-success/25';
const INFO = 'bg-info-tint text-info border border-info/25';
const ATTENTION = 'bg-warning-tint text-warning border border-warning/25';
const NEGATIVE = 'bg-danger-tint text-danger border border-danger/25';
const NEUTRAL = 'bg-surface-sunken text-ink-muted border border-hairline-strong';

/** Preset color schemes keyed by common status values. */
const STATUS_COLORS: Record<string, string> = {
  // Tenant statuses
  active: POSITIVE,
  suspended: ATTENTION,
  archived: NEUTRAL,

  // Case statuses
  draft: NEUTRAL,
  submitted: INFO,
  awaiting_documents: ATTENTION,
  processing: INFO,
  awaiting_provider_result: INFO,
  manual_review: ATTENTION,
  resubmission_requested: ATTENTION,
  approved: POSITIVE,
  approved_with_conditions: POSITIVE,
  rejected: NEGATIVE,
  expired: NEUTRAL,

  // Review task statuses
  open: INFO,
  assigned: INFO,
  in_progress: INFO,
  completed: POSITIVE,
  cancelled: NEUTRAL,

  // Member statuses
  disabled: NEGATIVE,
  invited: INFO,
  offboarded: NEUTRAL,

  // Support session statuses
  closed: NEUTRAL,
  terminated: NEGATIVE,

  // Domain statuses
  pending_verification: ATTENTION,
  failed: NEGATIVE,

  // Config promotion statuses
  promoted: POSITIVE,
  rolled_back: ATTENTION,

  // Monitoring alert statuses
  new: NEGATIVE,
  acknowledged: INFO,
  resolved: POSITIVE,
  dismissed: NEUTRAL,

  // Monitoring schedule statuses (active, paused, expired already covered)
  paused: ATTENTION,
};

/** Preset color schemes for priority values. */
const PRIORITY_COLORS: Record<string, string> = {
  critical: NEGATIVE,
  high: ATTENTION,
  medium: INFO,
  low: NEUTRAL,
};

interface StatusBadgeProps {
  /** The status or priority value to display. */
  value: string;
  /** Whether to use priority color scheme instead of status. */
  variant?: 'status' | 'priority';
}

export function StatusBadge({ value, variant = 'status' }: StatusBadgeProps) {
  const colorMap = variant === 'priority' ? PRIORITY_COLORS : STATUS_COLORS;
  const colorClasses = colorMap[value] ?? NEUTRAL;
  const displayLabel = value.replace(/_/g, ' ');

  return (
    <span
      className={`inline-flex items-center rounded-sm px-2 py-0.5 font-mono text-[0.6875rem] font-medium uppercase tracking-[0.04em] ${colorClasses}`}
    >
      {displayLabel}
    </span>
  );
}
